import { Building2, Mail } from "lucide-react";
import { useId, type FC } from "react";

import {
  AddressFields,
  fieldClass,
  FormRow,
  FormSection,
  FormSubheading,
  SwitchField,
  TextField
} from "@/components/form";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import type { IndividualEditValues } from "../../../api/individuals";
import { LinkedCompanyCard } from "../../companies/components/LinkedCompanyCard";
import { useCaseCompany } from "../../companies/queries";
import type { CaseSettingsForm } from "../useCaseSettingsForm";
import { SettingsLoadingPreview } from "./SettingsLoadingPreview";

/** Label over a switch with a Yes / No readout, sized like a text field. */
const InlineSwitch: FC<{
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  className?: string;
}> = ({ label, checked, onCheckedChange, className }) => {
  const id = useId();
  return (
    <div className={cn(fieldClass, "flex flex-col gap-2", className)}>
      <Label htmlFor={id}>{label}</Label>
      <div className="flex h-8 items-center gap-2">
        <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
        <span className="text-sm text-muted-foreground">
          {checked ? "Yes" : "No"}
        </span>
      </div>
    </div>
  );
};

export const EmployerInfoSection: FC<{ form: CaseSettingsForm }> = ({
  form
}) => {
  const company = useCaseCompany(form.caseId);
  if (!form.isReady || company.isLoading)
    return <SettingsLoadingPreview variant="employer" />;
  const linked = company.data?.company ?? null;

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-background">
      <div className="flex min-h-0 w-full flex-1 flex-col items-start overflow-auto">
        <FormSection
          variant="band"
          icon={Building2}
          title="Employer Information"
          className="[&>div:first-child]:border-t-0"
        >
          <SwitchField<IndividualEditValues>
            name="employerPetitioner"
            label="Is the employer the petitioner?"
            className="max-w-md"
          />

          {linked && form.caseId ? (
            <LinkedCompanyCard
              individualId={form.caseId}
              company={linked}
              link={company.data?.link ?? null}
            />
          ) : (
            <>
              <FormRow>
                <TextField<IndividualEditValues>
                  name="employerInfo.legalBusinessName"
                  label="Legal Business Name"
                  placeholder="Enter legal business name"
                  autoComplete="organization"
                />
                <InlineSwitch
                  label="Has DBA"
                  checked={form.isDbaEnabled}
                  onCheckedChange={form.setDbaEnabled}
                />
              </FormRow>

              {form.isDbaEnabled ? (
                <TextField<IndividualEditValues>
                  name="employerInfo.dba"
                  label="DBA"
                  placeholder="Enter DBA"
                />
              ) : null}
            </>
          )}
        </FormSection>

        {linked ? null : (
          <FormSection
            variant="band"
            icon={Mail}
            title="Employer Contact Information"
          >
            <FormRow>
              <TextField<IndividualEditValues>
                name="employerInfo.contactName"
                label="Contact Name"
                placeholder="Enter contact name"
              />
              <TextField<IndividualEditValues>
                name="employerInfo.contactTitle"
                label="Contact Title"
                placeholder="Enter contact title"
              />
            </FormRow>
            <FormRow>
              <TextField<IndividualEditValues>
                name="employerInfo.contactEmail"
                label="Contact Email"
                placeholder="Enter contact email"
                type="email"
              />
              <TextField<IndividualEditValues>
                name="employerInfo.contactPhone"
                label="Contact Phone"
                placeholder="Enter contact phone"
                type="tel"
              />
            </FormRow>

            <FormSubheading className="text-sm">
              Business Address
            </FormSubheading>
            <AddressFields<IndividualEditValues> name="employerInfo.businessAddress" />
          </FormSection>
        )}
      </div>
    </div>
  );
};
